import { useTranslation } from "react-i18next"

import { useNotification } from "@/contexts/notification-context"
import type { Organization } from "@/models/organization"
import { getAdminConfirmDeleteDialogDefaults } from "@/modules/admin/utils/get-admin-confirm-delete-dialog-defaults"
import { useDeleteBill } from "@/services/bill/use-delete-bill"
import type { Bill } from "@/services/core-api/types"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"
import { useConfirm } from "material-ui-confirm"

interface UseConfirmDeleteBillOptions {
  onSuccess?: (billId: Bill["id"]) => void
  organizationId: Organization["id"]
}

export const useConfirmDeleteBill = ({
  onSuccess,
  organizationId,
}: UseConfirmDeleteBillOptions) => {
  const { t } = useTranslation("common")
  const confirm = useConfirm()
  const { setNotification } = useNotification()
  const { deleteBillIsPending, deleteBillMutateAsync } = useDeleteBill()

  const confirmAndDeleteBill = async (billId: Bill["id"]): Promise<void> => {
    const { confirmed } = await confirm({
      title: t("deleteBill.confirmTitle"),
      description: t("deleteBill.confirmMessage"),
      ...getAdminConfirmDeleteDialogDefaults(t),
    })

    if (!confirmed) {
      return
    }

    try {
      await deleteBillMutateAsync({
        billId,
        organizationId,
      })
      onSuccess?.(billId)
    } catch (error: unknown) {
      setNotification({
        message: getErrorNotificationMessage(
          error,
          t("deleteBill.deleteBillError")
        ),
        type: "error",
      })
    }
  }

  return {
    confirmAndDeleteBill,
    deleteBillIsPending,
  }
}
